import { createContext, useContext, useState, useCallback, useEffect } from "react";
import translations from "../data/translations";
import { isBot } from "../utils/isBot";

const FLAGS = {
  it: { flag: "🇮🇹", label: "Italiano" },
  en: { flag: "🇬🇧", label: "English" },
  fr: { flag: "🇫🇷", label: "Français" },
  de: { flag: "🇩🇪", label: "Deutsch" },
  es: { flag: "🇪🇸", label: "Español" },
};

const LANGS = Object.keys(FLAGS);

const LanguageContext = createContext(null);

export const useLanguage = () => useContext(LanguageContext);
export { FLAGS, LANGS };

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    // Italian is the default: bots/renderers always get IT so the served HTML is
    // coherent with <html lang="it"> + Italian title/description. Humans only
    // switch language via the explicit selector (persisted in localStorage).
    if (isBot()) return "it";
    const saved = localStorage.getItem("hostland_lang");
    if (saved && LANGS.includes(saved)) return saved;
    return "it";
  });

  // Traduzione on-demand in corso (usato per mostrare una rotellina fluida
  // nel selettore lingua). Impostato da chi esegue il fetch delle traduzioni
  // (es. PropertyLanding) mentre la lingua viene tradotta e messa in cache.
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const changeLang = useCallback((newLang) => {
    if (LANGS.includes(newLang)) {
      setLang(newLang);
      localStorage.setItem("hostland_lang", newLang);
    }
  }, []);

  const t = useCallback((key) => {
    const entry = translations[key];
    if (!entry) return key;
    return entry[lang] || entry["it"] || key;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, changeLang, t, FLAGS, LANGS, translating, setTranslating }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
